import { useState, useRef, useEffect, useCallback } from 'react';
import type React from 'react';


import { Box } from 'ui/box';
import { Stack } from 'ui/stack';

import Button from 'components/partials/button/button';
import IconButton from 'components/partials/button/icon-button';

import { useCurrentUser } from 'hooks/api/use-current-user';

import { useApiGetCohorts } from '../../hooks/use-api-get-cohorts';

import Icons from 'assets/icons';

interface CohortLike {
  id?: string | number;
  name?: string;
}

interface CohortsListProps {
  cohorts?: CohortLike[];
  isLoading: boolean;
  isError: boolean;
}

const CohortsList = ({
  cohorts,
  isLoading,
  isError,
}: CohortsListProps): React.JSX.Element | React.JSX.Element[] => {
  if (isLoading) {
    return (
      <Stack
        direction="row"
        sx={{ alignItems: 'center', justifyContent: 'center' }}
      >
        <Icons.Spinner className="w-5 h-5 animate-spin text-gray-400" />
      </Stack>
    );
  }

  if (isError) {
    return (
      <p className="text-sm text-red-600 leading-5">Failed to load cohorts</p>
    );
  }

  if (!cohorts?.length) {
    return (
      <p className="text-sm text-gray-500 leading-5">No cohorts available</p>
    );
  }

  return cohorts.map((cohort) => (
    <p
      key={cohort.id ?? cohort.name}
      className="text-sm leading-5 text-gray-700 truncate"
    >
      {cohort.name}
    </p>
  ));
};

interface CohortsPopoverProps {
  clientId: string | number;
}

const CohortsPopover = ({
  clientId,
}: CohortsPopoverProps): React.JSX.Element => {
  const [isOpen, setIsOpen] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const currentUser = useCurrentUser();

  const {
    data: cohorts,
    isLoading,
    isError,
  } = useApiGetCohorts({
    oktaUserId: currentUser?.data?.oktaUserId as string,
    clientId,
    enabled: isOpen,
  });

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleClickOutside = (e: MouseEvent): void => {
      const target = e.target as Node | null;

      if (
        target &&
        !popoverRef.current?.contains(target) &&
        !buttonRef.current?.contains(target)
      ) {
        handleClose();
      }
    };

    const handleEscape = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') handleClose();
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, handleClose]);

  return (
    <Box sx={{ position: 'relative' }}>
      <Button
        ref={buttonRef as never}
        variant="reserve-invert"
        size="xs"
        className="shadow"
        onClick={() => setIsOpen((prev) => !prev)}
        data-testid="cohorts-button"
      >
        Cohorts
      </Button>

      {isOpen && (
        <Box
          ref={popoverRef}
          sx={{
            position: 'absolute',
            right: 0,
            top: '100%',
            mt: 0.5,
            width: '200px',
            bgcolor: 'common.white',
            borderWidth: '1px',
            borderColor: 'tokens.navy.100',
            borderRadius: '0.375rem',
            boxShadow: 8,
            zIndex: 50,
            py: 2,
            px: 2.5,
          }}
          role="dialog"
          aria-label="Cohorts"
        >
          <IconButton
            variant="neutral-text"
            size="baby"
            className="!absolute right-2 top-2"
            icon={<Icons.Close />}
            onClick={handleClose}
            aria-label="Close cohorts"
            data-testid="cohorts-close-button"
          />
          <h3 className="text-base font-semibold leading-6 text-navy-800 mb-2">
            Cohorts
          </h3>

          <Stack sx={{ gap: 1 }}>
            <CohortsList
              cohorts={cohorts as CohortLike[] | undefined}
              isLoading={isLoading}
              isError={isError}
            />
          </Stack>
        </Box>
      )}
    </Box>
  );
};

export default CohortsPopover;
